import { useEffect, useRef, useState } from 'react'
import { t } from '../i18n.ts'
import type { StorageMode } from '../store/types.ts'
import './NoteEditor.css'

const DEBOUNCE_MS = 600

/**
 * Free-text note for one concert.
 *
 * Saves on a debounce while typing and immediately on blur. When the store is
 * running in `ephemeral` mode — Safari private browsing, a full quota, a discarded
 * schema version — the "Lagret" confirmation is withheld and replaced with a
 * warning: a saved state that is not true is worse than no saved state at all, and
 * notes are the only irreplaceable thing in this app.
 */
export function NoteEditor({
  value,
  onSave,
  storageMode,
}: {
  value: string
  onSave: (note: string) => void
  storageMode: StorageMode
}) {
  const [draft, setDraft] = useState(value)
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const timer = useRef<number | null>(null)
  const latest = useRef(value)
  const pending = useRef<string | null>(null)
  const onSaveRef = useRef(onSave)
  onSaveRef.current = onSave

  /*
   * Adopt a change from another device — but never while the user is mid-edit.
   * `latest` only tracks the last committed value, so without the pending-timer
   * check a sync arriving between keystrokes would replace the textarea with the
   * remote text while the queued commit still published the local text, leaving
   * the field showing something that was never saved.
   */
  useEffect(() => {
    if (timer.current !== null) return
    if (value !== latest.current) {
      latest.current = value
      setDraft(value)
    }
  }, [value])

  useEffect(() => {
    return () => {
      // Flush rather than cancel. Tapping a link blurs first, but an iOS edge-swipe
      // back or a browser back button does not — and dropping the last 600ms of a
      // note is losing the only irreplaceable data in this app.
      if (timer.current !== null) {
        window.clearTimeout(timer.current)
        if (pending.current !== null && pending.current !== latest.current) {
          onSaveRef.current(pending.current)
        }
      }
    }
  }, [])

  function commit(next: string) {
    latest.current = next
    pending.current = null
    timer.current = null
    onSaveRef.current(next)
    setStatus('saved')
  }

  function onChange(next: string) {
    setDraft(next)
    setStatus('saving')
    pending.current = next
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => commit(next), DEBOUNCE_MS)
  }

  function onBlur() {
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = null
    if (draft !== latest.current) commit(draft)
  }

  const ephemeral = storageMode === 'ephemeral'

  return (
    <div className="note-editor">
      <div className="note-editor__head">
        <label className="note-editor__label" htmlFor="note">
          {t.concert.noteLabel}
        </label>
        {!ephemeral && status !== 'idle' && (
          <span className="note-editor__status" aria-live="polite">
            {status === 'saving' ? t.concert.noteSaving : t.concert.noteSaved}
          </span>
        )}
      </div>

      <textarea
        id="note"
        className="note-editor__input"
        value={draft}
        rows={3}
        placeholder={t.concert.notePlaceholder}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
      />

      {ephemeral && (
        <p className="note-editor__warning" role="status">
          <strong>{t.concert.noteEphemeral}.</strong> {t.concert.noteEphemeralHelp}
        </p>
      )}
    </div>
  )
}
